/**
 * list_connections tool - List connections in an environment
 */

import { z } from "zod";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";
import { ValidationError } from "../utils/errors.js";

export const listConnectionsInputSchema = z.object({
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ListConnectionsInput = z.infer<typeof listConnectionsInputSchema>;

export const listConnectionsTool = {
  name: "list_connections",
  description:
    "List all connections in an environment. Shows connection names, statuses (Connected/Error), connector types, and who created them. Use this to troubleshoot connection issues or see what services are connected.",
  inputSchema: {
    type: "object" as const,
    properties: {
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
  },
};

export interface ListConnectionsResult {
  id: string;
  displayName: string;
  connectorName: string;
  status: string;
  statusError?: string;
  createdTime: string;
  createdBy: string;
}

export interface ListConnectionsContext {
  api: ConnectorMetadataApi | null;
}

export async function handleListConnections(
  context: ListConnectionsContext,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  if (!context.api) {
    throw new ValidationError("Connector API not configured");
  }

  const parsed = listConnectionsInputSchema.parse(input);

  logger.info({ input: parsed }, "Executing list_connections");

  // The environment argument was parsed and then dropped, so every call answered about
  // the default environment regardless of what the caller asked for.
  const connections = await context.api.listConnections(parsed.environment);

  if (connections.length === 0) {
    return { text: "No connections found", structured: { count: 0, connected: [], errors: [] } };
  }

  // Group by status
  const connected = connections.filter(c => c.properties.statuses?.[0]?.status === "Connected");
  const errored = connections.filter(c => c.properties.statuses?.[0]?.status !== "Connected");

  // Filter out malformed connections (missing apiId or with internal-only connectors)
  const validErrors = errored.filter(conn => {
    const apiId = conn.properties.apiId;
    if (!apiId) return false;
    // Filter out internal system connectors that aren't user-facing
    if (apiId.includes('logicflows') || apiId.includes('commondataservice')) return false;
    return true;
  });

  const lines: string[] = [];

  if (connected.length > 0) {
    lines.push(`Connected (${connected.length}):`);
    for (const conn of connected) {
      const connectorType = getConnectorFriendlyName(conn.properties.apiId);
      lines.push(`  • ${connectorType}`);
      lines.push(`    Account: ${conn.properties.displayName}`);
      lines.push(`    ID: ${conn.name}`);
    }
  }

  if (validErrors.length > 0) {
    if (lines.length > 0) lines.push(``);
    lines.push(`Errors (${validErrors.length}):`);
    for (const conn of validErrors) {
      const connectorType = getConnectorFriendlyName(conn.properties.apiId);
      const error = conn.properties.statuses?.[0]?.error?.message || "Unknown error";
      lines.push(`  • ${connectorType}: ${error}`);
    }
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: connected.length + validErrors.length,
    connected: connected.map((conn) => ({
      id: conn.name,
      displayName: conn.properties.displayName,
      connector: getConnectorFriendlyName(conn.properties.apiId),
      ...(conn.properties.apiId ? { apiId: conn.properties.apiId } : {}),
    })),
    errors: validErrors.map((conn) => ({
      id: conn.name,
      displayName: conn.properties.displayName,
      connector: getConnectorFriendlyName(conn.properties.apiId),
      ...(conn.properties.apiId ? { apiId: conn.properties.apiId } : {}),
      error: conn.properties.statuses?.[0]?.error?.message || "Unknown error",
    })),
  };

  if (lines.length === 0) {
    return { text: "No connections found.", structured };
  }

  return { text: lines.join("\n"), structured };
}

function getConnectorFriendlyName(apiId?: string): string {
  if (!apiId) return "Unknown";

  const id = apiId.split("/").pop() || "";
  const names: Record<string, string> = {
    "shared_excelonlinebusiness": "Excel Online (Business)",
    "shared_office365": "Office 365 Outlook",
    "shared_sharepointonline": "SharePoint",
    "shared_teams": "Microsoft Teams",
    "shared_webcontentsv2": "HTTP with Entra ID",
    "shared_approvals": "Approvals",
    "shared_office365users": "Office 365 Users",
    "shared_onedriveforbusiness": "OneDrive for Business",
    "shared_planner": "Planner",
    "shared_microsoftforms": "Microsoft Forms",
  };

  return names[id] || id.replace("shared_", "").replace(/_/g, " ");
}
